import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Collapse, Input, InputNumber, Modal, Pagination, Progress, Segmented, Select, Space, Tag } from "antd";
import {
  CheckCircleFilled,
  ContactsOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { digitsFormatter, digitsParser } from "../../utils/digits";
import {
  downloadGuestTemplate,
  downloadRowsCsv,
  guestsFromContactPicker,
  COUNTRIES,
  normalizePhone,
  phoneCheck,
  phoneMatchKey,
  readGuestFile,
} from "../../utils/guestImport";

const VALIDATE_CHUNK = 200; // the server accepts up to 200 guests per request
const IMPORT_CHUNK = 100;
const PAGE_SIZE = 20;

// Reason codes that make a row unimportable. Anything else (e.g. no issue) is fine.
const BLOCKING_SERVER_STATUSES = ["duplicate", "invalid", "limit"];

// The server answers in camelCase, but be forgiving if it ever changes.
const readResults = (data) =>
  (data?.results || data?.Results || []).map((r) => ({
    index: r.index ?? r.Index,
    status: r.status ?? r.Status,
    reason: r.reason ?? r.Reason,
  }));

// The number is sent in full ("+<country code><number>"), so the server never has to guess the country.
const toPayload = (row, countryCode) => ({
  firstName: row.firstName,
  lastName: row.lastName,
  phone: normalizePhone(row.phone, countryCode) || row.phone,
  email: row.email,
  companions: row.companions || 0,
});

/**
 * Import many guests at once: from the phone's contact picker (Android Chrome) or from a
 * .csv / .vcf file. Both sources feed one review list, then the server adds them in batches.
 */
const ImportGuestsModal = ({ open, onClose, onDone, eventId, isWedding, enableVip, primaryColor, isMobile }) => {
  const { t, i18n } = useTranslation();
  const fileInput = useRef(null);
  const keyCounter = useRef(0);

  const [step, setStep] = useState("source"); // source | review | importing | done
  const [rows, setRows] = useState([]);
  const [registrationType, setRegistrationType] = useState("regular");
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Rows the host has edited stay in the top list (so a row does not jump away while being fixed).
  const [touched, setTouched] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [helpOpen, setHelpOpen] = useState(false);
  // Country used for numbers written without a country code (a number with its own +code keeps it).
  const [defaultCountry, setDefaultCountry] = useState("966");

  const supportsContactPicker =
    typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;

  const reset = () => {
    setStep("source");
    setRows([]);
    setRegistrationType("regular");
    setLoadError("");
    setTouched(new Set());
    setPage(1);
    setProgress({ done: 0, total: 0 });
    setDefaultCountry("966");
  };

  const handleClose = () => {
    const anyAdded = rows.some((r) => r.result?.status === "added");
    reset();
    onClose();
    if (anyAdded) onDone?.();
  };

  // ---------- problems found on each row ----------

  // For each row: the first thing wrong with it (or null). Repeats inside the list are flagged
  // on the later copies; the server flags guests who are already on the event's list.
  const issues = useMemo(() => {
    const seen = new Set();
    return rows.map((row) => {
      if (row.result?.status === "added") return null;
      if (!row.firstName.trim() && !row.lastName.trim()) return "name_missing";
      const check = phoneCheck(row.phone, defaultCountry);
      if (!check.phone) {
        const emailOk = !isWedding && /\S+@\S+\.\S+/.test(row.email || "");
        if (!emailOk) return check.error;
      } else {
        const key = phoneMatchKey(check.phone);
        if (seen.has(key)) return "repeated_in_file";
        seen.add(key);
      }
      if (row.server && BLOCKING_SERVER_STATUSES.includes(row.server.status)) return row.server.reason || "invalid";
      return null;
    });
  }, [rows, isWedding, defaultCountry]);

  const readyRows = rows.filter((_, i) => !issues[i]);
  const problemCount = rows.length - readyRows.length;

  // ---------- getting guests in ----------

  const checkAgainstEvent = async (list, countryCode = defaultCountry) => {
    // One dry run (nothing saved) so duplicates already on the event's list show up before importing.
    // Only the rows just added are checked; the verdicts are written onto the matching rows by key.
    setChecking(true);
    try {
      const verdicts = {};
      for (let i = 0; i < list.length; i += VALIDATE_CHUNK) {
        const chunk = list.slice(i, i + VALIDATE_CHUNK);
        const { data } = await axios.post(`${BASE_URL}/EventRegistration/bulk`, {
          eventId,
          registrationType,
          validateOnly: true,
          guests: chunk.map((row) => toPayload(row, countryCode)),
        });
        readResults(data).forEach((r) => {
          verdicts[chunk[r.index].key] = { status: r.status, reason: r.reason };
        });
      }
      setRows((prev) => prev.map((r) => (verdicts[r.key] ? { ...r, server: verdicts[r.key] } : r)));
    } catch (error) {
      // Not fatal: the server re-checks everything when importing.
    } finally {
      setChecking(false);
    }
  };

  // Adds guests to the review list. The first load starts the list; "Add more" keeps what is
  // already there, and repeated people are flagged by the normal duplicate check.
  const loadGuests = async (allGuests) => {
    // Spreadsheets often carry blank lines at the end; they are not guests.
    const guests = allGuests.filter((g) => [g.firstName, g.lastName, g.phone, g.email].some((v) => String(v || "").trim()));
    if (!guests.length) {
      setLoadError(t("eventRegistrations.import.nothingFound"));
      return;
    }
    setLoadError("");
    const list = guests.map((g) => ({
      key: ++keyCounter.current,
      firstName: g.firstName || "",
      lastName: g.lastName || "",
      phone: g.phone || "",
      email: g.email || "",
      companions: g.companions || 0,
      server: null,
      result: null,
    }));
    const adding = step === "review";
    setRows((prev) => (adding ? [...prev, ...list] : list));
    if (!adding) setPage(1);
    setStep("review");
    await checkAgainstEvent(list);
  };

  // Changing the country re-reads every number, so the duplicate check is run again for the new numbers.
  const handleCountryChange = (code) => {
    setDefaultCountry(code);
    if (rows.length) {
      setRows((prev) => prev.map((row) => ({ ...row, server: null })));
      checkAgainstEvent(rows.filter((row) => !row.result), code);
    }
  };

  const isArabic = i18n.language?.startsWith("ar");
  const countryPicker = (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 6, fontWeight: 500 }}>{t("eventRegistrations.import.defaultCountry")}</div>
      <Select
        showSearch
        optionFilterProp="label"
        value={defaultCountry}
        onChange={handleCountryChange}
        style={{ width: "100%" }}
        options={COUNTRIES.map((c) => ({ value: c.code, label: "+" + c.code + "  " + (isArabic ? c.ar : c.en) }))}
      />
      <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>{t("eventRegistrations.import.defaultCountryHint")}</div>
    </div>
  );

  const handlePickContacts = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });
      if (!picked || picked.length === 0) return;
      await loadGuests(guestsFromContactPicker(picked));
    } catch (error) {
      if (error?.name !== "AbortError") setLoadError(t("eventRegistrations.import.contactPickerFailed"));
    }
  };

  const handleFileChosen = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ""; // lets the same file be chosen again
    if (files.length === 0) return;
    try {
      // Several files can be picked at once; they all go into the same list.
      const lists = await Promise.all(files.map((file) => readGuestFile(file)));
      await loadGuests(lists.flat());
    } catch (error) {
      setLoadError(t("eventRegistrations.import.readFailed"));
    }
  };

  // ---------- editing the review list ----------

  const updateRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, server: null } : r)));
  const startEditing = (key) => setTouched((prev) => new Set(prev).add(key));
  const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  // ---------- saving ----------

  const runImport = async (targets) => {
    setStep("importing");
    setProgress({ done: 0, total: targets.length });
    const outcome = {};

    for (let i = 0; i < targets.length; i += IMPORT_CHUNK) {
      const chunk = targets.slice(i, i + IMPORT_CHUNK);
      try {
        const { data } = await axios.post(`${BASE_URL}/EventRegistration/bulk`, {
          eventId,
          registrationType,
          validateOnly: false,
          guests: chunk.map((row) => toPayload(row, defaultCountry)),
        });
        readResults(data).forEach((r) => {
          outcome[chunk[r.index].key] = { status: r.status, reason: r.reason };
        });
      } catch (error) {
        chunk.forEach((r) => {
          outcome[r.key] = { status: "error", reason: "save_failed" };
        });
      }
      setProgress({ done: Math.min(i + IMPORT_CHUNK, targets.length), total: targets.length });
    }

    setRows((prev) => prev.map((r) => (outcome[r.key] ? { ...r, result: outcome[r.key] } : r)));
    setStep("done");
  };

  const addedCount = rows.filter((r) => r.result?.status === "added").length;
  const failedRows = rows.filter((r) => r.result && r.result.status === "error");
  const skippedRows = rows.filter((r, i) => r.result?.status !== "added" && (issues[i] || r.result));

  // Phone reasons carry how many digits were found / expected, e.g. "found 8, need 9".
  const reasonText = (code, row) => {
    const check = row ? phoneCheck(row.phone, defaultCountry) : {};
    return t(`eventRegistrations.import.reasons.${code}`, { defaultValue: code, found: check.found, expected: check.expected });
  };

  // Rows with a problem, as a CSV the host can send back to whoever supplied the list.
  const downloadProblems = (list) =>
    downloadRowsCsv(
      list.map((row) => {
        const i = rows.indexOf(row);
        const code = row.result?.reason || issues[i] || "error";
        return { ...row, reason: reasonText(code, row) };
      }),
      !isWedding
    );

  // ---------- screens ----------

  const sourceScreen = (
    <div>
      {!isWedding && enableVip && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>{t("eventRegistrations.import.registrationType")}</div>
          <Segmented
            block
            value={registrationType}
            onChange={setRegistrationType}
            options={[
              { value: "regular", label: t("eventRegistrations.import.regular") },
              { value: "vip", label: t("eventRegistrations.import.vip") },
            ]}
          />
        </div>
      )}

      {countryPicker}

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {supportsContactPicker && (
          <Button
            block
            size="large"
            type="primary"
            icon={<ContactsOutlined />}
            onClick={handlePickContacts}
            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            {t("eventRegistrations.import.chooseContacts")}
          </Button>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            block
            size="large"
            type={supportsContactPicker ? "default" : "primary"}
            icon={<UploadOutlined />}
            onClick={() => fileInput.current?.click()}
            style={supportsContactPicker ? undefined : { backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            {t("eventRegistrations.import.uploadFile")}
          </Button>
          <Button
            size="large"
            icon={<InfoCircleOutlined />}
            onClick={() => setHelpOpen(true)}
            aria-label={t("eventRegistrations.import.iphoneTitle")}
          />
        </div>

        <div style={{ color: "#8c8c8c", fontSize: 12 }}>{t("eventRegistrations.import.uploadHint")}</div>

        <Button type="link" icon={<DownloadOutlined />} onClick={() => downloadGuestTemplate(!isWedding)} style={{ padding: 0 }}>
          {t("eventRegistrations.import.template")}
        </Button>
      </Space>

      {loadError && <Alert style={{ marginTop: 16 }} type="warning" showIcon message={loadError} />}
    </div>
  );

  // Top list: rows with a problem, plus rows the host is editing. Everything else is tucked away below.
  const visibleIndexes = rows.map((_, i) => i).filter((i) => issues[i] || touched.has(rows[i].key));
  const passedIndexes = rows.map((_, i) => i).filter((i) => !issues[i] && !touched.has(rows[i].key));
  const pageIndexes = visibleIndexes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const rowCard = (i) => {
    const row = rows[i];
    const issue = issues[i];
    const cleaned = normalizePhone(row.phone, defaultCountry);
    return (
      <div
        key={row.key}
        style={{
          border: `1px solid ${issue ? "#ffccc7" : "#b7eb8f"}`,
          background: issue ? "#fff2f0" : "#f6ffed",
          borderRadius: 10,
          padding: 10,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Input
            style={{ flex: "1 1 140px", minWidth: 0 }}
            value={row.firstName}
            placeholder={t("eventRegistrations.import.firstName")}
            onChange={(e) => updateRow(row.key, { firstName: e.target.value })}
          />
          <Input
            style={{ flex: "1 1 140px", minWidth: 0 }}
            value={row.lastName}
            placeholder={t("eventRegistrations.import.lastName")}
            onChange={(e) => updateRow(row.key, { lastName: e.target.value })}
          />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
          <Input
            style={{ flex: "1 1 180px", minWidth: 0 }}
            dir="ltr"
            inputMode="tel"
            value={row.phone}
            placeholder={t("eventRegistrations.import.phone")}
            onChange={(e) => updateRow(row.key, { phone: e.target.value })}
            status={issue && issue.startsWith("phone") ? "error" : undefined}
          />
          <InputNumber
            min={0}
            precision={0}
            parser={digitsParser}
            formatter={digitsFormatter(isArabic)}
            value={row.companions}
            onChange={(v) => updateRow(row.key, { companions: v ?? 0 })}
            style={{ width: 72 }}
            title={t("eventRegistrations.import.companions")}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeRow(row.key)}
            aria-label={t("eventRegistrations.import.remove")}
          />
        </div>
        <div style={{ marginTop: 6, fontSize: 12, display: "flex", flexWrap: "wrap", gap: 8, overflowWrap: "anywhere" }}>
          {issue ? (
            <span style={{ color: "#cf1322" }}>{reasonText(issue, row)}</span>
          ) : (
            <span dir="ltr" style={{ color: "#389e0d" }}>
              <CheckCircleFilled /> {cleaned || row.email}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Rows that passed: one compact line each, read-only until the host taps edit.
  const passedList = (
    <div style={{ maxHeight: 320, overflowY: "auto" }}>
      {passedIndexes.map((i) => {
        const row = rows[i];
        const cleaned = normalizePhone(row.phone, defaultCountry);
        return (
          <div
            key={row.key}
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, padding: "6px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}
          >
            <span style={{ flex: "1 1 120px", minWidth: 0, overflowWrap: "anywhere" }}>
              {`${row.firstName} ${row.lastName}`.trim() || row.phone}
            </span>
            <span dir="ltr" style={{ color: "#389e0d" }}>
              {cleaned || row.email}
            </span>
            {row.companions > 0 && <Tag style={{ marginInlineEnd: 0 }}>+{row.companions}</Tag>}
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing(row.key)} aria-label={t("eventRegistrations.import.edit")} />
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeRow(row.key)} aria-label={t("eventRegistrations.import.remove")} />
          </div>
        );
      })}
    </div>
  );

  const reviewScreen = (
    <div>
      {countryPicker}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 12 }}>
        <Tag color="green">{t("eventRegistrations.import.ready", { count: readyRows.length })}</Tag>
        {problemCount > 0 && <Tag color="red">{t("eventRegistrations.import.needAttention", { count: problemCount })}</Tag>}
        {checking && <Tag>{t("eventRegistrations.import.checking")}</Tag>}
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        {supportsContactPicker && (
          <Button icon={<ContactsOutlined />} onClick={handlePickContacts}>
            {t("eventRegistrations.import.addMoreContacts")}
          </Button>
        )}
        <Button icon={<UploadOutlined />} onClick={() => fileInput.current?.click()}>
          {t("eventRegistrations.import.addMoreFiles")}
        </Button>
      </Space>
      {loadError && <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={loadError} />}

      {problemCount > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          message={t("eventRegistrations.import.problemsBanner", { count: problemCount })}
          description={
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadProblems(rows.filter((_, i) => issues[i]))}
              style={{ marginTop: 6 }}
            >
              {t("eventRegistrations.import.downloadProblems")}
            </Button>
          }
        />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pageIndexes.map(rowCard)}
      </div>

      {passedIndexes.length > 0 && (
        <Collapse
          style={{ marginTop: 12 }}
          defaultActiveKey={visibleIndexes.length === 0 ? ["passed"] : []}
          items={[
            {
              key: "passed",
              label: (
                <span>
                  <CheckCircleFilled style={{ color: "#52c41a" }} />{" "}
                  {t("eventRegistrations.import.passedTitle", { count: passedIndexes.length })}
                </span>
              ),
              children: passedList,
            },
          ]}
        />
      )}

      {visibleIndexes.length > PAGE_SIZE && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <Pagination
            simple
            current={page}
            pageSize={PAGE_SIZE}
            total={visibleIndexes.length}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );

  const importingScreen = (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
      <Progress
        type="circle"
        percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
        strokeColor={primaryColor}
      />
      <div style={{ marginTop: 16 }}>
        {t("eventRegistrations.import.importing", { done: progress.done, total: progress.total })}
      </div>
    </div>
  );

  const doneScreen = (
    <div>
      <Alert
        type={addedCount > 0 ? "success" : "warning"}
        showIcon
        message={t("eventRegistrations.import.addedCount", { count: addedCount })}
        description={
          skippedRows.length > 0 ? t("eventRegistrations.import.skippedCount", { count: skippedRows.length }) : undefined
        }
      />
      {skippedRows.length > 0 && (
        <Button block icon={<DownloadOutlined />} onClick={() => downloadProblems(skippedRows)} style={{ marginTop: 12 }}>
          {t("eventRegistrations.import.downloadProblems")}
        </Button>
      )}
      {skippedRows.length > 0 && (
        <div style={{ marginTop: 12, maxHeight: 280, overflowY: "auto" }}>
          {skippedRows.map((row) => {
            const i = rows.indexOf(row);
            const code = row.result?.reason || issues[i] || "error";
            return (
              <div
                key={row.key}
                style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}
              >
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {`${row.firstName} ${row.lastName}`.trim() || row.phone}
                </span>
                <span style={{ color: "#cf1322", flexShrink: 0 }}>{reasonText(code, row)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ---------- footer buttons per step ----------

  let footer = null;
  if (step === "review") {
    // Full-width buttons that wrap, so both stay reachable on a phone.
    footer = (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
        <Button style={isMobile ? { flex: "1 1 100%" } : undefined} onClick={() => { setRows([]); setTouched(new Set()); setStep("source"); }}>
          {t("eventRegistrations.import.back")}
        </Button>
        <Button
          type="primary"
          disabled={readyRows.length === 0 || checking}
          onClick={() => runImport(readyRows)}
          style={{ backgroundColor: primaryColor, borderColor: primaryColor, ...(isMobile ? { flex: "1 1 100%", whiteSpace: "normal", height: "auto", minHeight: 32 } : {}) }}
        >
          {problemCount > 0
            ? t("eventRegistrations.import.importBtnSkipped", { count: readyRows.length, skipped: problemCount })
            : t("eventRegistrations.import.importBtn", { count: readyRows.length })}
        </Button>
      </div>
    );
  } else if (step === "done") {
    footer = [
      failedRows.length > 0 && (
        <Button key="retry" onClick={() => runImport(failedRows)}>
          {t("eventRegistrations.import.retryFailed", { count: failedRows.length })}
        </Button>
      ),
      <Button key="close" type="primary" onClick={handleClose} style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
        {t("eventRegistrations.import.close")}
      </Button>,
    ].filter(Boolean);
  }

  const iphoneQuickSteps = [1, 2, 3, 4].map((n) => t(`eventRegistrations.import.iphoneQuick${n}`));
  const iphoneSteps = [1, 2, 3, 4, 5, 6].map((n) => t(`eventRegistrations.import.iphoneStep${n}`));

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".csv,.xlsx,.vcf,text/csv,text/vcard,text/x-vcard,text/directory,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={handleFileChosen}
      />
      <Modal
        title={t("eventRegistrations.import.title")}
        open={open}
        onCancel={step === "importing" ? undefined : handleClose}
        closable={step !== "importing"}
        maskClosable={false}
        footer={footer}
        width={isMobile ? "calc(100vw - 16px)" : 640}
        style={isMobile ? { top: 12 } : undefined}
        styles={{ body: { maxHeight: isMobile ? "72vh" : "70vh", overflowY: "auto", overflowX: "hidden" } }}
        destroyOnClose
      >
        {step === "source" && sourceScreen}
        {step === "review" && reviewScreen}
        {step === "importing" && importingScreen}
        {step === "done" && doneScreen}
      </Modal>

      <Modal
        title={t("eventRegistrations.import.iphoneTitle")}
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={<Button onClick={() => setHelpOpen(false)}>{t("eventRegistrations.import.close")}</Button>}
        width={isMobile ? "calc(100vw - 16px)" : 480}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("eventRegistrations.import.iphoneQuickHeading")}</div>
        <ol style={{ paddingInlineStart: 20, margin: 0, lineHeight: 1.9 }}>
          {iphoneQuickSteps.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ol>
        <div style={{ fontWeight: 600, margin: "16px 0 6px" }}>{t("eventRegistrations.import.iphoneListHeading")}</div>
        <ol style={{ paddingInlineStart: 20, margin: 0, lineHeight: 1.9 }}>
          {iphoneSteps.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ol>
        <div style={{ marginTop: 12, color: "#8c8c8c", fontSize: 12 }}>{t("eventRegistrations.import.iphoneNote")}</div>
      </Modal>
    </>
  );
};

export default ImportGuestsModal;
